import { useState } from "react";
import type {
  VarietyPricing,
} from "../../services/varietyPricingApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Edit2 } from "lucide-react";
import { useSubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "../../services/setupApprovalApi";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface EditVarietyPricingDialogProps {
  pricing: VarietyPricing;
}

export const EditVarietyPricingDialog = ({
  pricing,
}: EditVarietyPricingDialogProps) => {
  const [open, setOpen] = useState(false);

  const [formData, setFormData] = useState({
    companySeedlingPrice: (pricing.companySeedlingPrice ?? "").toString(),
    propagationServicePrice: (pricing.propagationServicePrice ?? "").toString(),
    isActive: pricing.isActive,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const submitMutation = useSubmitSetupApproval();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const companySeedlingPrice = parseFloat(formData.companySeedlingPrice);
    const propagationServicePrice = parseFloat(formData.propagationServicePrice);
    const hasCompany = !isNaN(companySeedlingPrice) && companySeedlingPrice > 0;
    const hasPropagation = !isNaN(propagationServicePrice) && propagationServicePrice > 0;

    if (!hasCompany && !hasPropagation) {
      notify.error("Set at least one price — seedling or propagation.");
      return;
    }

    const payload = {
      id: pricing.id,
      companySeedlingPrice: hasCompany ? companySeedlingPrice : null,
      propagationServicePrice: hasPropagation ? propagationServicePrice : null,
      isActive: formData.isActive,
    };

    confirm({
      title: "Submit pricing changes for approval?",
      description: `Changes to pricing for ${pricing.varietyName ?? "this variety"} will be submitted for approval before they take effect.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitMutation
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.Pricing,
            entityJson: JSON.stringify(payload),
            existingEntityId: pricing.id,
            description: `Update Pricing: ${pricing.varietyName ?? pricing.id}`,
          })
          .then(() => {
            notify.success("Submitted for approval");
            setOpen(false);
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          <Edit2 className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Variety Pricing</DialogTitle>
          <DialogDescription>
            {pricing.varietyName} &mdash;{" "}
            {pricing.pricingMode === 2
              ? "Per Seedling/Piece"
              : pricing.trayTypeName || (pricing.trayType === 210 ? "210-Hole Tray" : "442-Hole Tray")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {submitMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {submitMutation.error.message}
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium px-2 py-1 rounded bg-muted text-muted-foreground">
              {pricing.pricingModeName || (pricing.pricingMode === 2 ? "Per Seedling/Piece" : "Per Tray")}
            </span>
            {pricing.trayTypeName && (
              <span className="text-xs font-medium px-2 py-1 rounded bg-muted text-muted-foreground">
                {pricing.trayTypeName}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            To change pricing mode, deactivate this record and create a new one.
          </p>
          <p className="text-xs text-muted-foreground">
            Leave a price blank to mark that side as <span className="font-medium">not offered</span> for this variety. At least one of the two prices must be set.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel htmlFor="companySeedlingPrice">
                Seedling Price
              </FieldLabel>
              <Input
                id="companySeedlingPrice"
                type="number"
                step="0.01"
                min="0"
                value={formData.companySeedlingPrice}
                onChange={(e) => handleChange("companySeedlingPrice", e.target.value)}
                disabled={submitMutation.isPending}
              />
              {fieldErrors.companySeedlingPrice && (
                <div className="text-red-500 text-xs mt-1">
                  {fieldErrors.companySeedlingPrice.join(", ")}
                </div>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="propagationServicePrice">
                Propagation Price
              </FieldLabel>
              <Input
                id="propagationServicePrice"
                type="number"
                step="0.01"
                min="0"
                value={formData.propagationServicePrice}
                onChange={(e) => handleChange("propagationServicePrice", e.target.value)}
                disabled={submitMutation.isPending}
              />
              {fieldErrors.propagationServicePrice && (
                <div className="text-red-500 text-xs mt-1">
                  {fieldErrors.propagationServicePrice.join(", ")}
                </div>
              )}
            </Field>
          </div>

          <Field>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    isActive: checked as boolean,
                  }))
                }
                disabled={submitMutation.isPending}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Active
              </Label>
            </div>
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
